import React from 'react';
import styled from 'styled-components';

interface ImageModalProps {
  isOpen: boolean;
  imageUrl: string;
  imageAlt: string;
  title?: string;
  onClose: () => void;
  // Галерея изображений (опционально)
  images?: string[];
  currentIndex?: number;
  onNavigate?: (newIndex: number) => void;
}

const ImageModal: React.FC<ImageModalProps> = ({
  isOpen,
  imageUrl,
  imageAlt,
  title,
  onClose,
  images,
  currentIndex = 0,
  onNavigate
}) => {
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          if (images && images.length > 1 && onNavigate) {
            const newIndex = currentIndex > 0 ? currentIndex - 1 : images.length - 1;
            onNavigate(newIndex);
          }
          break;
        case 'ArrowRight':
          if (images && images.length > 1 && onNavigate) {
            const newIndex = currentIndex < images.length - 1 ? currentIndex + 1 : 0;
            onNavigate(newIndex);
          }
          break;
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Блокируем скролл страницы
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, images, currentIndex, onNavigate]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const handlePrevImage = () => {
    if (images && images.length > 1 && onNavigate) {
      const newIndex = currentIndex > 0 ? currentIndex - 1 : images.length - 1;
      onNavigate(newIndex);
    }
  };

  const handleNextImage = () => {
    if (images && images.length > 1 && onNavigate) {
      const newIndex = currentIndex < images.length - 1 ? currentIndex + 1 : 0;
      onNavigate(newIndex);
    }
  };

  const hasGallery = images && images.length > 1;

  return (
    <ModalOverlay onClick={handleOverlayClick} onKeyDown={handleKeyDown} tabIndex={0}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        {title && (
          <ModalHeader>
            <ModalTitle>{title}</ModalTitle>
            <CloseButton onClick={onClose}>×</CloseButton>
          </ModalHeader>
        )}
        {!title && (
          <TopCloseButton onClick={onClose}>×</TopCloseButton>
        )}
        <ModalBody $hasHeader={!!title}>
          <ModalImage src={imageUrl} alt={imageAlt} />
          
          {hasGallery && (
            <>
              <PrevButton onClick={handlePrevImage}>
                &#8249;
              </PrevButton>
              <NextButton onClick={handleNextImage}>
                &#8250;
              </NextButton>
              <ImageCounter>
                {currentIndex + 1} / {images.length}
              </ImageCounter>
            </>
          )}
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease-in-out;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background: rgba(0, 25, 0, 0.95);
  border: 2px solid #00d4ff;
  border-radius: 12px;
  max-width: 90vw;
  max-height: 90vh;
  box-shadow: 0 10px 30px rgba(0, 212, 255, 0.3);
  animation: scaleIn 0.3s ease-in-out;
  overflow: hidden;
  position: relative;
  
  @keyframes scaleIn {
    from { 
      opacity: 0;
      transform: scale(0.9);
    }
    to { 
      opacity: 1;
      transform: scale(1);
    }
  }
  
  @media (max-width: 768px) {
    max-width: 95vw;
    max-height: 85vh;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.2);
  background: rgba(0, 25, 0, 0.8);
`;

const ModalTitle = styled.h3`
  font-family: 'Courier New', monospace;
  color: #00d4ff;
  margin: 0;
  font-size: 18px;
  font-weight: bold;
  text-transform: uppercase;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #888;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    color: #ff4444;
  }
`;

const TopCloseButton = styled(CloseButton)`
  position: absolute;
  top: 15px;
  right: 15px;
  z-index: 10;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 50%;
  color: #fff;
  
  &:hover {
    background: rgba(255, 68, 68, 0.8);
    color: #fff;
  }
`;

const ModalBody = styled.div<{ $hasHeader: boolean }>`
  padding: ${props => props.$hasHeader ? '20px' : '0'};
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.2);
  min-height: ${props => props.$hasHeader ? '300px' : '400px'};
  position: relative;
  
  @media (max-width: 768px) {
    min-height: ${props => props.$hasHeader ? '250px' : '300px'};
  }
`;

const ModalImage = styled.img`
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  
  @media (max-width: 768px) {
    max-height: 60vh;
  }
`;

const NavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.7);
  border: 2px solid #00d4ff;
  color: #00d4ff;
  font-size: 24px;
  font-weight: bold;
  padding: 15px 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;
  
  &:hover {
    background: rgba(0, 212, 255, 0.2);
    color: #fff;
    transform: translateY(-50%) scale(1.1);
  }
  
  &:active {
    transform: translateY(-50%) scale(0.95);
  }
  
  @media (max-width: 768px) {
    font-size: 20px;
    padding: 10px 15px;
  }
`;

const PrevButton = styled(NavigationButton)`
  left: 20px;
  border-radius: 50% 8px 8px 50%;
  
  @media (max-width: 768px) {
    left: 10px;
  }
`;

const NextButton = styled(NavigationButton)`
  right: 20px;
  border-radius: 8px 50% 50% 8px;
  
  @media (max-width: 768px) {
    right: 10px;
  }
`;

const ImageCounter = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.7);
  border: 1px solid #00d4ff;
  color: #00d4ff;
  padding: 8px 16px;
  border-radius: 20px;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  font-weight: bold;
  backdrop-filter: blur(5px);
  
  @media (max-width: 768px) {
    bottom: 15px;
    font-size: 12px;
    padding: 6px 12px;
  }
`;

export default ImageModal;